import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SubjectIcon } from "@/components/SubjectIcon";
import { Button } from "@/components/ui/button";
import { Timer, ListChecks, Gauge, Play, Sparkle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const DIFF_COLOR = { facile: "#042A65", moyen: "#FFB800", difficile: "#FF4B0A" };

export default function Simulateur() {
  const navigate = useNavigate();
  const [sims, setSims] = useState(null);
  const [devoirs, setDevoirs] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get("/simulator/available").then((r) => setSims(r.data)).catch(() => setSims([]));
    api.get("/student/devoirs").then((r) => setDevoirs(r.data)).catch(() => setDevoirs([]));
  }, []);

  const launch = async (s) => {
    setLoadingId(s.id);
    try {
      const { data } = await api.post("/simulator/generate", { exam_id: s.exam_id, subject: s.subject });
      const dur = data.duration_min || s.duration_min;
      navigate(`/app/quiz/${encodeURIComponent(data.quiz_id)}?mode=exam&duration=${dur}`);
    } catch {
      toast.error("Génération IA indisponible, lancement du sujet standard.");
      navigate(`/app/quiz/${encodeURIComponent(s.id)}?mode=exam&duration=${s.duration_min}`);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Examens blancs chronométrés</h1>
        <p className="mt-1 text-ink/65">Placez-vous en conditions réelles : questions générées par l'IA, minuteur, correction et analyse de vos erreurs.</p>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-gold/40 bg-gold/5 px-4 py-3 text-sm text-ink/80" data-testid="ai-sim-note">
        <Sparkle weight="fill" className="text-gold" size={18} />
        Chaque examen blanc est <span className="font-semibold">généré par l'IA (Claude)</span> — des questions inédites à chaque tentative, infiniment rejouable.
      </div>

      {devoirs.length > 0 && (
        <div data-testid="devoirs-section">
          <h2 className="font-display text-xl font-bold">Devoirs encadrés</h2>
          <p className="mb-3 text-sm text-ink/65">Assignés par votre enseignant.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {devoirs.map((d) => (
              <div key={d.id} data-testid={`devoir-${d.id}`} className="rounded-2xl border border-forest/30 bg-forest/5 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold">{d.title}</div>
                    <div className="text-xs text-muted-foreground">Par {d.teacher_name}{d.due_at ? ` · à rendre le ${new Date(d.due_at).toLocaleDateString("fr-FR")}` : ""}</div>
                  </div>
                  {d.done && <span className="rounded-full bg-forest/10 px-2 py-0.5 text-xs font-semibold text-forest">Fait · {d.last_score}%</span>}
                </div>
                <Button data-testid={`devoir-launch-${d.id}`} onClick={() => navigate(`/app/quiz/${encodeURIComponent(d.quiz_id)}?mode=exam&duration=20`)}
                  className="mt-4 rounded-full bg-forest text-white hover:bg-forest/90">
                  <Play weight="fill" className="mr-1" /> {d.done ? "Refaire" : "Composer"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sims === null && <div className="h-40 animate-pulse rounded-2xl bg-sand" />}

      {sims && sims.length === 0 && (
        <div className="rounded-2xl border border-border bg-white p-8 text-center text-muted-foreground" data-testid="sim-empty">
          Aucun simulateur disponible pour votre examen pour le moment. De nouveaux sujets arrivent bientôt.
        </div>
      )}

      {sims && sims.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {sims.map((s) => (
            <div key={s.id} data-testid={`sim-card-${s.id}`} className="flex flex-col rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(4,42,101,0.1)]">
              <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
                  <SubjectIcon name={s.subject} size={24} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{s.subject}</div>
                  <div className="truncate text-sm text-muted-foreground">{s.title}</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                <span className="inline-flex items-center gap-1 rounded-full bg-cream px-3 py-1"><Timer size={14} /> {s.duration_min} min</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-cream px-3 py-1"><ListChecks size={14} /> {s.count} questions</span>
                <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 capitalize" style={{ background: `${DIFF_COLOR[s.difficulty] || "#FFB800"}1a`, color: DIFF_COLOR[s.difficulty] || "#FFB800" }}>
                  <Gauge size={14} /> {s.difficulty}
                </span>
              </div>
              <Button data-testid={`sim-launch-${s.id}`} disabled={loadingId === s.id} onClick={() => launch(s)} className="mt-5 rounded-full bg-forest text-white hover:bg-forest/90">
                {loadingId === s.id ? (
                  <><CircleNotch className="mr-1 animate-spin" /> Génération IA…</>
                ) : (
                  <><Play weight="fill" className="mr-1" /> Lancer l'examen blanc</>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
